import { Link, Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { SoundToggle } from '../components/SoundToggle'
import { fetchSession, useAdminStats, useLogout } from '../lib/admin-api'
import { useConfig } from '../lib/queries'

/**
 * Layout and gate for everything under /admin.
 *
 * The check runs in `beforeLoad`, so an unauthenticated visitor is redirected
 * before any admin component mounts or any admin query fires. The server
 * enforces this independently — this only exists so the UI does not flash a
 * broken page on the way to a 401.
 */
export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.fetchQuery({
      queryKey: ['session'],
      queryFn: fetchSession,
      staleTime: 30_000,
    })

    if (!session.authenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: AdminLayout,
})

// `exact` on Overview only, so /admin/add does not also light up /admin.
const NAV = [
  { to: '/admin', label: 'Overview', exact: true },
  { to: '/admin/requests', label: 'Requests', exact: false },
  { to: '/admin/add', label: 'Add keys', exact: false },
  { to: '/admin/library', label: 'Library', exact: false },
  { to: '/admin/taxonomy', label: 'Stores & sources', exact: false },
  { to: '/admin/deleted', label: 'Deleted', exact: false },
  { to: '/admin/sounds', label: 'Sounds', exact: false },
] as const

function AdminLayout() {
  const logout = useLogout()
  const navigate = useNavigate()
  const stats = useAdminStats()
  const config = useConfig()
  const pending = stats.data?.pendingRequests ?? 0

  return (
    <div className="grain min-h-dvh">
      <header className="sticky top-0 z-40 border-b border-line bg-surface-0">
        <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 sm:flex-nowrap sm:px-6 sm:py-3">
          <Link to="/admin" className="font-display text-sm font-semibold tracking-tight">
            {/* Named from the same setting as the public side, rather than
                spelling it out again and drifting the moment it changes. */}
            <span className="hidden sm:inline">{config.data?.siteName ?? 'Keys'}</span>
            <span className="sm:hidden">Keys</span>
            <span className="ml-2 rounded bg-accent-dim px-1.5 py-0.5 font-mono text-[10px] tracking-wider text-accent-strong uppercase">
              admin
            </span>
          </Link>

          {/* Scrolls sideways on a phone rather than stacking into three rows
              and pushing the page it labels off the screen. */}
          <nav className="no-scrollbar -mx-4 order-last flex w-[calc(100%+2rem)] items-center gap-1 overflow-x-auto px-4 sm:order-none sm:mx-0 sm:w-auto sm:overflow-visible sm:px-0">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.exact }}
                className="shrink-0 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap text-ink-soft transition-colors hover:bg-surface-2 hover:text-ink sm:px-3"
                activeProps={{ className: 'bg-surface-2 text-ink' }}
              >
                {item.label}
                {item.to === '/admin/requests' && pending > 0 ? (
                  <span className="ml-1.5 rounded-full bg-accent px-1.5 py-0.5 font-mono text-[10px] font-semibold text-surface-0">
                    {pending}
                  </span>
                ) : null}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <SoundToggle className="size-8" />
            <Link
              to="/"
              className="hidden text-xs text-ink-muted transition-colors hover:text-ink-soft sm:inline"
            >
              View site
            </Link>
            <button
              type="button"
              onClick={() => logout.mutate(undefined, { onSuccess: () => navigate({ to: '/login' }) })}
              className="rounded-md border border-line px-2.5 py-1.5 text-xs text-ink-soft transition-colors hover:border-line-strong hover:text-ink"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-[1400px] px-4 py-5 sm:px-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  )
}
